import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const AdminInviteCodeManager = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newCodeOptions, setNewCodeOptions] = useState({
    maxUses: 1,
    expiresInDays: 30,
    prefix: 'EYE'
  });
  
  useEffect(() => {
    fetchCodes();
  }, []);
  
  const fetchCodes = async () => {
    try {
      const response = await api.get('/admin/invite-codes');
      setCodes(response.data.codes);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch invite codes:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const generateCode = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/admin/invite-codes/generate', newCodeOptions);
      setCodes([response.data.code, ...codes]);
      
      // Update stats
      if (stats) {
        setStats({
          ...stats,
          total_codes: stats.total_codes + 1,
          active_codes: stats.active_codes + 1
        });
      }
    } catch (error) {
      console.error('Failed to generate code:', error);
    } finally {
      setGenerating(false);
    }
  };
  
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    // You could add a toast notification here
  };
  
  const disableCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to disable this invite code?')) {
      return;
    }
    
    try {
      await api.post(`/admin/invite-codes/${codeId}/disable`);
      // Refresh the codes list
      fetchCodes();
    } catch (error) {
      console.error('Failed to disable invite code:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-400"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h4 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">Total Codes</h4>
            <p className={`text-2xl font-bold text-gray-800 dark:text-[#f5f5f5]`}>{stats.total_codes}</p>
          </div>
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h4 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">Active</h4>
            <p className="text-2xl font-bold text-green-400">{stats.active_codes}</p>
          </div>
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h4 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">Used</h4>
            <p className="text-2xl font-bold text-yellow-400">{stats.fully_used_codes}</p>
          </div>
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h4 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">Expired</h4>
            <p className="text-2xl font-bold text-red-400">{stats.expired_codes}</p>
          </div>
        </div>
      )}
      
      {/* Code Generation */}
      <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-6">
        <h2 className={`heading-mono text-xl mb-6 text-gray-800 dark:text-[#f5f5f5]`}>Generate New Code</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className={`block text-mono text-sm mb-2 text-gray-700 dark:text-[#c4c4c4]`}>
              Prefix
            </label>
            <input
              type="text"
              value={newCodeOptions.prefix}
              onChange={(e) => setNewCodeOptions({
                ...newCodeOptions,
                prefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3)
              })}
              className="w-full border border-gray-300 dark:border-[#2e2e2e] bg-white dark:bg-[#1f1f1f] rounded px-4 py-3 rounded-lg text-mono uppercase"
              placeholder="EYE"
              maxLength="3"
              pattern="[A-Z]{3}"
              title="Prefix must be exactly 3 uppercase letters"
            />
          </div>
          
          <div>
            <label className={`block text-mono text-sm mb-2 text-gray-700 dark:text-[#c4c4c4]`}>
              Max Uses
            </label>
            <input
              type="number"
              min="1"
              max="1000"
              value={newCodeOptions.maxUses}
              onChange={(e) => setNewCodeOptions({
                ...newCodeOptions,
                maxUses: parseInt(e.target.value) || 1
              })}
              className="w-full border border-gray-300 dark:border-[#2e2e2e] bg-white dark:bg-[#1f1f1f] rounded px-4 py-3 rounded-lg text-mono"
            />
          </div>
          
          <div>
            <label className={`block text-mono text-sm mb-2 text-gray-700 dark:text-[#c4c4c4]`}>
              Days Until Expiry
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={newCodeOptions.expiresInDays}
              onChange={(e) => setNewCodeOptions({
                ...newCodeOptions,
                expiresInDays: parseInt(e.target.value) || 30
              })}
              className="w-full border border-gray-300 dark:border-[#2e2e2e] bg-white dark:bg-[#1f1f1f] rounded px-4 py-3 rounded-lg text-mono"
            />
          </div>
          
          <div className="flex items-end">
            <button 
              onClick={generateCode} 
              disabled={generating}
              className="w-full bg-blue-500 text-white hover:bg-blue-600 py-3 rounded-lg text-mono font-medium disabled:opacity-50"
            >
              {generating ? 'Generating...' : 'Generate Code'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Codes List */}
      <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-6">
        <h2 className={`heading-mono text-xl mb-6 text-gray-800 dark:text-[#f5f5f5]`}>Active Invite Codes</h2>
        
        <div className="space-y-3">
          {codes.filter(code => 
            code.current_uses < code.max_uses && 
            (!code.expires_at || new Date(code.expires_at) > new Date())
          ).map(code => (
            <div key={code.id} className="bg-gray-50 dark:bg-[#262626] p-4 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <code className="text-mono text-lg font-bold text-accent-400">
                      {code.code}
                    </code>
                    <button 
                      onClick={() => copyToClipboard(code.code)}
                      className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] text-xs px-3 py-1"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => disableCode(code.id)}
                      className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] text-xs px-3 py-1 text-red-400 hover:bg-red-400/10"
                    >
                      Disable
                    </button>
                  </div>
                  <div className={`text-mono text-xs space-y-1 text-gray-600 dark:text-[#c4c4c4]`}>
                    <div>Uses: {code.current_uses} / {code.max_uses}</div>
                    <div>Expires: {new Date(code.expires_at).toLocaleDateString()}</div>
                    {code.creator_email && <div>Created by: {code.creator_email}</div>}
                    <div>Created: {new Date(code.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                
                {code.used_by_email && (
                  <div className={`text-mono text-xs text-gray-600 dark:text-[#c4c4c4]`}>
                    Last used by: {code.used_by_email}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {codes.filter(code => 
          code.current_uses < code.max_uses && 
          (!code.expires_at || new Date(code.expires_at) > new Date())
        ).length === 0 && (
          <p className={`text-mono text-center py-8 text-gray-600 dark:text-[#c4c4c4]`}>
            No active invite codes. Generate one above.
          </p>
        )}
      </div>
      
      {/* Expired/Used Codes */}
      <details className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-6">
        <summary className={`heading-mono text-lg cursor-pointer text-gray-800 dark:text-[#f5f5f5]`}>
          Expired & Used Codes ({codes.filter(code => 
            code.current_uses >= code.max_uses || 
            (code.expires_at && new Date(code.expires_at) <= new Date())
          ).length})
        </summary>
        
        <div className="mt-4 space-y-3">
          {codes.filter(code => 
            code.current_uses >= code.max_uses || 
            (code.expires_at && new Date(code.expires_at) <= new Date())
          ).map(code => (
            <div key={code.id} className="bg-gray-50 dark:bg-[#262626] p-4 rounded-lg opacity-60">
              <div className="flex justify-between items-start">
                <div>
                  <code className="text-mono text-lg font-bold text-gray-500 dark:text-[#999999]">
                    {code.code}
                  </code>
                  <div className={`text-mono text-xs mt-2 text-gray-600 dark:text-[#c4c4c4]`}>
                    {code.current_uses >= code.max_uses ? 'Fully used' : 
                     new Date(code.expires_at) <= new Date() ? 'Expired' : 'Disabled'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

export default AdminInviteCodeManager;